import React, { useCallback, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormHelperText,
} from "@mui/material";
import { Controller, UseFormReturn } from "react-hook-form";
import { ProposalDetailsFormFields } from "@/modules/property-tracks/hooks/rental-financial-proposal/multi-step-form.hook";
import NwChip from "@/modules/common/components/ui-kit/chip";
import CustomCheckbox from "@/modules/common/components/ui/checkbox";

interface Props {
  options: Record<string, string[]>;
  form: UseFormReturn<ProposalDetailsFormFields>;
}

type SpecsRecord = Record<string, string[]>;

// Helper to toggle item in mainSpecs (keeps empty arrays)
const toggleItemInMainSpecs = (
  specs: SpecsRecord,
  category: string,
  item: string
): SpecsRecord => {
  const selected = specs[category] || [];
  const isSelected = selected.includes(item);
  const updated = isSelected
    ? selected.filter((i) => i !== item)
    : [...selected, item];

  return { ...specs, [category]: updated };
};

// Helper to toggle item in extraSpecs (removes empty arrays)
const toggleItemInExtraSpecs = (
  specs: SpecsRecord,
  category: string,
  item: string
): SpecsRecord => {
  const selected = specs[category] || [];
  const isSelected = selected.includes(item);
  const updated = isSelected
    ? selected.filter((i) => i !== item)
    : [...selected, item];

  if (updated.length === 0) {
    const { [category]: _, ...rest } = specs;
    return rest;
  }

  return { ...specs, [category]: updated };
};

// Helper to remove category from specs
const removeCategoryFromSpecs = (
  specs: SpecsRecord,
  category: string
): SpecsRecord => {
  const { [category]: _, ...rest } = specs;
  return rest;
};

// Subcomponents
interface OptionRowProps {
  label: string;
  isChecked: boolean;
  isDisabled?: boolean;
  onToggle: () => void;
  isEditable?: boolean;
  onLabelChange?: (value: string) => void;
}

const OptionRow: React.FC<OptionRowProps> = ({
  label,
  isChecked,
  isDisabled = false,
  onToggle,
  isEditable = false,
  onLabelChange,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      py: (t) => t.nawy.spacing.xxs,
    }}
  >
    <CustomCheckbox
      checked={isChecked}
      disabled={isDisabled}
      onChange={onToggle}
    />
    {isEditable ? (
      <TextField
        size="small"
        variant="standard"
        placeholder="Extra option"
        value={label}
        onChange={(e) => onLabelChange?.(e.target.value)}
        sx={{ ml: 1, flex: 1 }}
        InputProps={{
          disableUnderline: true,
          sx: {
            padding: 0,
            "& input": { padding: 0, margin: 0 },
          },
        }}
      />
    ) : (
      <Typography>{label}</Typography>
    )}
  </Box>
);

const MainSpecsFilter: React.FC<Props> = ({ options, form }) => {
  const [extraInputs, setExtraInputs] = useState<SpecsRecord>(
    () => form.getValues("extraSpecs") || {}
  );

  const addExtraInput = useCallback((category: string) => {
    setExtraInputs((prev) => ({
      ...prev,
      [category]: [...(prev[category] || []), ""],
    }));
  }, []);

  const clearCategoryInputs = useCallback((category: string) => {
    setExtraInputs((prev) => removeCategoryFromSpecs(prev, category));
  }, []);

  const canAddMoreInputs = useCallback(
    (category: string) => {
      const categoryInputs = extraInputs[category];
      return (
        !categoryInputs?.length ||
        categoryInputs[categoryInputs.length - 1]?.trim()
      );
    },
    [extraInputs]
  );

  return (
    <Controller
      name="mainSpecs"
      control={form.control}
      defaultValue={{}}
      render={({
        field: { value: mainSpecs = {}, onChange: onMainSpecsChange },
        fieldState: { error: mainSpecsError },
      }) => (
        <Controller
          name="extraSpecs"
          control={form.control}
          defaultValue={{}}
          render={({
            field: { value: extraSpecs = {}, onChange: onExtraSpecsChange },
          }) => {
            const activeCategories = Object.keys(mainSpecs);

            const handleCategoryToggle = (category: string) => {
              const isActive = mainSpecs[category] !== undefined;

              if (isActive) {
                onMainSpecsChange(removeCategoryFromSpecs(mainSpecs, category));
                onExtraSpecsChange(
                  removeCategoryFromSpecs(extraSpecs, category)
                );
                clearCategoryInputs(category);
              } else {
                onMainSpecsChange({ ...mainSpecs, [category]: [] });
              }
            };

            const handleMainSpecToggle = (category: string, item: string) => {
              onMainSpecsChange(
                toggleItemInMainSpecs(mainSpecs, category, item)
              );
            };

            const handleExtraSpecToggle = (category: string, item: string) => {
              onExtraSpecsChange(
                toggleItemInExtraSpecs(extraSpecs, category, item)
              );
            };

            const handleExtraInputChange = (
              category: string,
              index: number,
              newValue: string
            ) => {
              const oldValue = extraInputs[category]?.[index] || "";

              // Update local input state
              setExtraInputs((prev) => {
                const updated = [...(prev[category] || [])];
                updated[index] = newValue;
                return { ...prev, [category]: updated };
              });

              // Update form if old value was checked
              const selected = extraSpecs[category] || [];
              if (selected.includes(oldValue)) {
                const trimmedNew = newValue.trim();
                let updatedSpecs: SpecsRecord;

                if (trimmedNew) {
                  // Replace old value with new
                  updatedSpecs = {
                    ...extraSpecs,
                    [category]: selected.map((item) =>
                      item === oldValue ? trimmedNew : item
                    ),
                  };
                } else {
                  // Remove if empty
                  const filtered = selected.filter((item) => item !== oldValue);
                  updatedSpecs =
                    filtered.length > 0
                      ? { ...extraSpecs, [category]: filtered }
                      : removeCategoryFromSpecs(extraSpecs, category);
                }

                onExtraSpecsChange(updatedSpecs);
              }
            };
            return (
              <Box>
                {mainSpecsError && (
                  <FormHelperText error>
                    {mainSpecsError.message}
                  </FormHelperText>
                )}

                {/* Category Chips */}
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                  {Object.keys(options).map((category) => (
                    <NwChip
                      key={category}
                      label={category}
                      color={
                        mainSpecs[category] !== undefined
                          ? "primary"
                          : "default"
                      }
                      variant="outlined"
                      onClick={() => handleCategoryToggle(category)}
                      clickable
                    />
                  ))}
                </Box>

                {/* Category Sections */}
                {activeCategories.map((category) => {
                  const mainSelected = mainSpecs[category] || [];
                  const extraSelected = extraSpecs[category] || [];
                  const categoryExtraInputs = extraInputs[category] || [];

                  return (
                    <Box
                      key={category}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        mb: 2,
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={(t) => ({
                          ...t.nawy.typography.english.variants.body.xs.regular,
                          color: t.nawy.colors.semantic.text.secondary,
                        })}
                      >
                        {category}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          border: (t) =>
                            `1px solid ${t.nawy.colors.semantic.border.secondary}`,
                          borderRadius: (t) => t.nawy.radius.lg,
                          "& > *:not(:last-child)": {
                            borderBottom: (t) =>
                              `1px solid ${t.nawy.colors.semantic.border.secondary}`,
                          },
                        }}
                      >
                        {/* Predefined Options */}
                        {options[category]?.map((item, idx) => (
                          <OptionRow
                            key={`predefined-${idx}-${item}`}
                            label={item}
                            isChecked={mainSelected.includes(item)}
                            onToggle={() =>
                              handleMainSpecToggle(category, item)
                            }
                          />
                        ))}

                        {/* Extra Options */}
                        {categoryExtraInputs.map((inputValue, idx) => {
                          const trimmed = inputValue.trim();
                          const isDisabled = !trimmed;
                          const isChecked =
                            !isDisabled && extraSelected.includes(inputValue);

                          return (
                            <OptionRow
                              key={`extra-${idx}`}
                              label={inputValue}
                              isChecked={isChecked}
                              isDisabled={isDisabled}
                              onToggle={() =>
                                !isDisabled &&
                                handleExtraSpecToggle(category, inputValue)
                              }
                              isEditable
                              onLabelChange={(value) =>
                                handleExtraInputChange(category, idx, value)
                              }
                            />
                          );
                        })}
                      </Box>

                      {/* Add Option Button */}
                      {canAddMoreInputs(category) && (
                        <Box>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => addExtraInput(category)}
                          >
                            + Add option
                          </Button>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            );
          }}
        />
      )}
    />
  );
};

export default MainSpecsFilter;